import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icons from '../../../../../../../components/Common/Icons/Icons';
import { Button } from '../../../../../../../components/Common/UI/Button';
import { StepProps } from '../../index';
import { RadioGroup, RadioGroupItem } from '../../../../../../../components/Common/UI/Radio';
import Label from '../../../../../../../components/Common/UI/Label';
import { cn } from '../../../../../../../utils/styles';
import { guidanceUser } from '../../../../../../../contstant';
import Checkbox from '../../../../../../../components/Common/UI/Checkbox';
import { getGuidanceSettingsFromStorage } from '../../../../../../../components/Pages/Home/SettingsModal/storage-utils';
import { EnrollmentStepType } from '../../../../../../../components/Pages/Home/SettingsModal';
import { Input } from '../../../../../../../components/Common/UI/Input';

interface AccountCardProps extends React.PropsWithChildren {
  name: string;
  accNumber: string;
  variant?: EnrollmentStepType;
}

type NotifyMeByType = 'email' | 'sms' | 'email-sms';
type DeliveryType = 'electronic' | 'paper' | 'both';

const labelStyles = 'text-base text-primary-text';

function AccountCard({ accNumber, children, variant }: AccountCardProps) {
  const [notifyMeBy, setNotifyMeBy] = useState<NotifyMeByType>('email');
  const settings = getGuidanceSettingsFromStorage();

  const [deliveryTypeMonthly, setDeliveryTypeMonthly] = useState<DeliveryType>(
    settings.deliveryTypeMonthly ?? 'electronic',
  );
  const [deliveryTypeVisa, setDeliveryTypeVisa] = useState<DeliveryType>(settings.deliveryTypeVisa ?? 'electronic');

  const handleMonthlyChange = (val: DeliveryType) => {
    setDeliveryTypeMonthly(val);
  };

  const handleVisaChange = (val: DeliveryType) => {
    setDeliveryTypeVisa(val);
  };

  return (
    <div className="border rounded-lg bg-white p-4 sm:p-6 child:mb-4 last:child:mb-0">
      {variant === 'by-account' ? (
        <div className="flex gap-4 items-center">
          <div>
            <Icons.AccountsFilled />
          </div>
          <div>
            <h2 className="text-lg font-bold">Account Number: {accNumber}</h2>
          </div>
        </div>
      ) : null}

      <div id="form-items" className="flex flex-col gap-4">
        <RadioGroup defaultValue="agree" className="flex gap-4 flex-col sm:flex-row">
          {[
            { value: 'agree', label: 'I agree to receive paperless statements and/or notices.' },
            {
              value: 'not-agree',
              label: 'I do not agree and would like to receive paper statements and/or notices.',
            },
          ].map(({ value, label }) => (
            <div key={value} className="flex items-center space-x-2 child:m-0 ">
              <RadioGroupItem value={value} id={`option-${value}-${accNumber}`} variant="secondary" />
              <Label htmlFor={`option-${value}-${accNumber}`} className={cn('text-sm')}>
                {label}
              </Label>
            </div>
          ))}
        </RadioGroup>

        {variant === 'by-account' ? (
          <Input
            label="Email Address"
            labelClassName={labelStyles}
            defaultValue={guidanceUser.email}
            className="max-w-[325px]"
          />
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {variant === 'by-account' ? (
            <>
              <div>
                <Label className={labelStyles}>Delivery Type: (Monthly Statement)</Label>

                <RadioGroup
                  defaultValue="electronic"
                  className="flex gap-4 flex-col sm:flex-row"
                  value={deliveryTypeMonthly}
                  onValueChange={val => handleMonthlyChange(val as DeliveryType)}
                >
                  {[
                    { value: 'electronic', label: 'Electronic' },
                    { value: 'paper', label: 'Paper' },
                    { value: 'both', label: 'Both' },
                  ].map(({ value, label }) => (
                    <div key={value} className="flex items-center space-x-2 child:m-0">
                      <RadioGroupItem value={value} id={`option-${value}-${accNumber}`} variant="secondary" />
                      <Label htmlFor={`option-${value}-${accNumber}`} className={cn('text-sm')}>
                        {label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label className={labelStyles}>Delivery Type: (Visa)</Label>

                <RadioGroup
                  defaultValue="electronic"
                  className="flex gap-4 flex-col sm:flex-row"
                  value={deliveryTypeVisa}
                  onValueChange={val => handleVisaChange(val as DeliveryType)}
                >
                  {[
                    { value: 'electronic', label: 'Electronic' },
                    { value: 'paper', label: 'Paper' },
                    { value: 'both', label: 'Both' },
                  ].map(({ value, label }) => (
                    <div key={value} className="flex items-center space-x-2 child:m-0">
                      <RadioGroupItem value={value} id={`option-${value}-${accNumber}-visa`} variant="secondary" />
                      <Label htmlFor={`option-${value}-${accNumber}-visa`} className={cn('text-sm')}>
                        {label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </>
          ) : null}

          <div>
            <Label className={labelStyles}>Notify Me By:</Label>

            <RadioGroup
              defaultValue="email"
              className="flex gap-4 flex-col sm:flex-row"
              defaultChecked
              value={notifyMeBy}
              onValueChange={val => setNotifyMeBy(val as NotifyMeByType)}
            >
              {[
                { value: 'email', label: 'Email' },
                { value: 'sms', label: 'SMS' },
                { value: 'email-sms', label: 'Email and SMS' },
              ].map(({ value, label }) => (
                <div key={value} className="flex items-center space-x-2 child:m-0">
                  <RadioGroupItem value={value} id={`option-${value}-${accNumber}`} variant="secondary" />
                  <Label htmlFor={`option-${value}-${accNumber}`} className={cn('text-sm')}>
                    {label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        </div>

        <div className="flex gap-8 child:flex-grow flex-col sm:flex-row">
          {notifyMeBy === 'email' || notifyMeBy === 'email-sms' ? (
            <Input
              label="Email Address"
              labelClassName={labelStyles}
              defaultValue={guidanceUser.email}
              className="max-w-[325px]"
            />
          ) : null}
          {notifyMeBy === 'sms' || notifyMeBy === 'email-sms' ? (
            <Input
              label="Phone Number"
              labelClassName={labelStyles}
              defaultValue={guidanceUser.contactInformation.officePhone}
              className="max-w-[325px]"
            />
          ) : null}
        </div>

        {variant === 'by-account' ? (
          <div>
            <div className="flex items-center space-x-2 child:m-0 mb-2">
              <Checkbox id={`attach-statements-${accNumber}`} className="text-secondary" variant="secondary" />
              <Label htmlFor={`attach-statements-${accNumber}`} className="mb-0">
                Attach Statements
              </Label>
            </div>
            <p className="text-[#EF9F00] text-sm">
              Check this box to start receiving all statements as an attachment to the email address registered with
              this account.
            </p>
          </div>
        ) : null}
      </div>

      {children}
    </div>
  );
}
AccountCard.defaultProps = {
  variant: 'by-account',
};

const accountNumbers = ['XXXXXX9999', 'XXXXXX9876'];

export default function Step3Enrollment({ handleNextStep }: StepProps) {
  const navigate = useNavigate();
  const { enrollmentStep3 } = getGuidanceSettingsFromStorage();

  const handleSubmit = () => {
    sessionStorage.setItem('enrollment-completed', JSON.stringify(true));
    navigate('/guidance/financial-institution/application');
  };

  return (
    <>
      <div
        className="flex gap-3 border bg-orange-50 text-[#EF9F00] text-lg font-semibold rounded-[10px]
      p-4 sm:px-7 sm:py-5"
      >
        <Icons.SolarHistoryBold width={28} height={28} />
        <span>In order to complete your enrollment, you MUST click “Submit”.</span>
      </div>

      {/* If by account we show all accounts, if global - only one card. */}
      {(enrollmentStep3 === 'by-account' ? accountNumbers : ['XXXXXX9999']).map((accNum, index) => (
        <AccountCard
          key={accNum}
          name={`account${index}`}
          accNumber={accNum}
          variant={enrollmentStep3 || 'by-account'}
        />
      ))}

      <div className="flex gap-4 justify-end items-center">
        <Button className="bg-[#51CF7C] hover:bg-[#51CF7CE6]" onClick={handleSubmit}>
          SUBMIT
        </Button>
      </div>
    </>
  );
}
